import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Field } from "formik"

import getAppConfig from "~/utils/getAppConfig"
import { apiAreas, apiClients, apiPacks } from "~/routes"
import useFetchResource from "~/hooks/useFetchResource"

import CatchCard from "~/library/CatchCard"
import Area from "~/types/Area"
import Pack from "~/types/Pack"
import Booking from "~/types/Booking"

import * as Yup from "yup"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikTextarea from "~/library/FormikTextarea"
import FormikRadioGroup from "~/library/FormikRadioGroup"
import CurrencyInput from "~/library/CurrencyInput"
import DateSelector from "~/library/DateSelector"
import LoadingWrapper from "~/library/LoadingWrapper"
import SelectOrCreate from "~/library/SelectOrCreate"

const bookingSchema = Yup.object({
  areaId: Yup.string().required("An area selection is required"),
  date: Yup.string().required("A booking date is required"),
  name: Yup.string().required("A booking name is required"),
  details: Yup.string().nullable(),
  packIds: Yup.array().of(Yup.string()).optional(),
})

interface BookingFormProps {
  onSubmit: (values: any) => void
  initialValues?: Booking | null
  isEditing?: boolean
}

const BookingForm: React.FC<BookingFormProps> = ({
  onSubmit,
  initialValues,
  isEditing = false,
}) => {
  const { venueSlug } = getAppConfig()
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [availability, setAvailability] = useState<Area[]>([])
  const [isBookedAreasLoading, setIsBookedAreasLoading] = useState(false)

  const clientsEndpoint = apiClients.index.path({ venue_slug: venueSlug })
  const packsEndpoint = apiPacks.index.path({ venue_slug: venueSlug })

  const { data: packs } = useFetchResource<{ packs: any[] }, "packs">(packsEndpoint, "packs", {
    scope: "active",
  })

  useEffect(() => {
    if (isEditing && initialValues?.date) {
      setSelectedDate(initialValues.date)
    }
  }, [initialValues, isEditing])

  useEffect(() => {
    if (selectedDate) {
      setIsBookedAreasLoading(true)
      const req = apiAreas.availability({
        venue_slug: venueSlug,
        query: { date: selectedDate },
      })
      req.then(({ areas }) => {
        setAvailability(areas)
      })
      req.finally(() => setIsBookedAreasLoading(false))
    }
  }, [venueSlug, selectedDate])

  const defaultValues = {
    name: "",
    clientId: "",
    clientAttributes: {
      name: "",
      email: "",
      phone: "",
    },
    areaId: "",
    anticipatedRevenueCents: 0,
    date: null,
    packIds: [],
  }

  // Handle pack selection change
  const handlePackChange = (packId: string, field: any) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        field.onChange({
          target: {
            name: field.name,
            value: [...(field.value || []), packId],
          },
        })
      } else {
        field.onChange({
          target: {
            name: field.name,
            value: field.value?.filter((id: string) => id !== packId) || [],
          },
        })
      }
    }
  }

  // Prepare initial values with existing pack IDs for editing
  const formInitialValues =
    isEditing && initialValues
      ? {
          ...initialValues,
          packIds: initialValues.packs?.map((pack) => pack.uuid) || [],
          areaId: initialValues.areaId || initialValues.area?.id || "",
        }
      : defaultValues

  return (
    <FormikWrapper
      onSubmit={onSubmit}
      validationSchema={bookingSchema}
      onValuesChange={({ date }: any) => setSelectedDate(date)}
      initialValues={formInitialValues}
    >
      <h4>Booking Details</h4>
      <CatchCard>
        <FormikInput
          name="name"
          label="Booking Name"
          placeholder="Woodworking Enthusiests Outing"
          required
          helpText=""
        />

        <CurrencyInput
          name="anticipatedRevenueCents"
          label="Anticipated Revenue"
          placeholder="8,6753.09"
          currency="USD"
        />
        <FormikTextarea
          name="details"
          label="Additional Details"
          placeholder="Client likes scotch, hates computers"
          helpText=""
          maxLength={1000}
        />
      </CatchCard>
      <h4>Client Details</h4>
      <CatchCard>
        {isEditing && initialValues?.client && (
          <>
            <p className="small">To update client details go to the clients page</p>
            <div className="field">
              <label>Name</label>
              <p className="form-control disabled"> {initialValues.client.name} </p>
            </div>
            <div className="field">
              <label>Email</label>
              <p className="form-control disabled"> {initialValues.client.email} </p>
            </div>
            <div className="field">
              <label>Phone</label>
              <p className="form-control disabled"> {initialValues.client.phone} </p>
            </div>
            <p className="mt-3">Change Client</p>
          </>
        )}

        <SelectOrCreate
          name="clientId"
          attributesPrefix="clientAttributes"
          placeholder="Search or Create a Client"
          endpoint={clientsEndpoint}
          responseKey="clients"
          labelKey="name"
          searchParam="name_or_email_cont"
          allowCreate={!isEditing}
          createFields={[
            { name: "name", label: "Name", type: "text", required: true },
            { name: "email", label: "Email", type: "email", required: true },
            { name: "phone", label: "Phone", type: "text" },
          ]}
        />
      </CatchCard>

      <h4>Select Packs</h4>
      <CatchCard>
        <LoadingWrapper isLoading={!packs}>
          <Field name="packIds">
            {({ field }: any) => (
              <div>
                {packs && packs.length > 0 ? (
                  packs.map((pack: Pack) => {
                    const isSelected = field.value?.includes(pack.uuid)
                    return (
                      <label
                        key={pack.uuid}
                        htmlFor={`pack-${pack.uuid}`}
                        className={`
                          card border-2 mb-3 cursor-pointer
                          ${isSelected ? "border-primary" : "border-gray"}
                          transition-all
                        `}
                      >
                        <div className="card-body p-3">
                          <input
                            type="checkbox"
                            id={`pack-${pack.uuid}`}
                            value={pack.uuid}
                            checked={isSelected}
                            onChange={handlePackChange(pack.uuid, field)}
                            className="form-check-input d-none"
                          />
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h5 className="card-title mb-0">{pack.name}</h5>
                            <span className="text-muted">
                              ${(pack.priceCents / 100).toFixed(2)}
                            </span>
                          </div>
                          {pack.items && pack.items.length > 0 && (
                            <div className="text-muted" style={{ fontSize: 13 }}>
                              {pack.items.map((item: any, index: number) => (
                                <span key={item.id}>
                                  {item.quantity > 1 && `${item.quantity}x `}
                                  {item.lineItem?.name || "Item"}
                                  {index < pack.items.length - 1 && ", "}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </label>
                    )
                  })
                ) : (
                  <div className="text-muted text-center p-4">No packs available</div>
                )}
              </div>
            )}
          </Field>
        </LoadingWrapper>
      </CatchCard>

      <div>
        <h4>Select a Date</h4>
        <DateSelector
          name="date"
          isFormik={true}
          futureOnly={true}
          disabled={isBookedAreasLoading}
        />
      </div>
      <h4>Area</h4>
      <CatchCard>
        <LoadingWrapper isLoading={isBookedAreasLoading}>
          <div className="row"></div>
          <FormikRadioGroup
            name="areaId"
            variant="card"
            options={availability.map((a: any) => {
              const isBooked = a.isBooked && a.id != initialValues?.areaId
              return {
                value: a.id,
                label: `${a.name}${isBooked ? " (Booked)" : ""}`,
                disabled: isBooked,
                description: `Capacity: ${a.capacity}`,
              }
            })}
            emptyState={
              <div className="d-flex align-items-center justify-content-center">
                Date must be selected
              </div>
            }
          />
        </LoadingWrapper>
      </CatchCard>
      <div className="d-flex">
        <div className="ms-auto">
          <Link className="btn btn-secondary me-2" to="/bookings">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
        </div>
      </div>
    </FormikWrapper>
  )
}

export default BookingForm
